const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

/** تبدیل ارقام لاتین به فارسی */
export const fa = (v: string | number | null | undefined): string =>
  v === null || v === undefined ? '—' : String(v).replace(/\d/g, (d) => FA_DIGITS[Number(d)])

/** تبدیل ارقام فارسی/عربی به لاتین (برای ورودی‌ها) */
export const toEnDigits = (v: string): string =>
  String(v ?? '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
   .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))

const trimZeros = (s: string) => (s.includes('.') ? s.replace(/\.?0+$/, '') : s)

/** عدد با جداکننده هزارگان و ارقام فارسی */
export function num(value: number | undefined | null, digits = 0): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  const abs = Math.abs(value)
  let d = digits
  if (!digits) {
    if (abs === 0) d = 0
    else if (abs < 0.0001) d = 8
    else if (abs < 0.01) d = 6
    else if (abs < 1) d = 4
    else if (abs < 1000) d = 2
    else d = 0
  }
  const s = trimZeros(value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d }))
  return fa(s)
}

/** مبلغ تومانی */
export const irt = (v?: number | null, digits = 0) => `${num(v, digits)} تومان`

/** خلاصه‌سازی اعداد بزرگ: ۱٫۲ میلیارد */
export function compact(v?: number | null): string {
  if (!v) return '۰'
  const units: [number, string][] = [
    [1e12, 'هزار میلیارد'],
    [1e9, 'میلیارد'],
    [1e6, 'میلیون'],
    [1e3, 'هزار'],
  ]
  for (const [size, label] of units) {
    if (Math.abs(v) >= size) return `${num(v / size, 2)} ${label}`
  }
  return num(v)
}

export const pct = (v?: number | null) => `${v && v > 0 ? '+' : ''}${num(v ?? 0, 2)}٪`

export const changeClass = (v?: number | null) =>
  (v ?? 0) > 0 ? 'text-up' : (v ?? 0) < 0 ? 'text-down' : 'text-slate-400'

/**
 * تاریخ شمسی
 *
 * ⚠ `Intl.DateTimeFormat().format()` روی تاریخ نامعتبر **RangeError** پرتاب
 * می‌کند. چون این تابع مستقیم داخل JSX صدا زده می‌شود، یک مقدار خرابِ آمده از
 * سرور کافی بود تا کل صفحه از کار بیفتد و کاربر ببیند «صفحه باز نمی‌شود».
 * پس هر ورودیِ غیرقابل تفسیر به «—» تبدیل می‌شود.
 */
export function faDate(iso?: string | null, withTime = true): string {
  const d = parseDate(iso)
  if (!d) return '—'
  try {
    const date = new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
    if (!withTime) return date
    const time = new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit' }).format(d)
    return `${date} — ${time}`
  } catch {
    return '—'
  }
}

/** تفسیر امن رشته تاریخ؛ خروجی null یعنی قابل استفاده نیست. */
export function parseDate(iso?: string | null): Date | null {
  if (!iso || typeof iso !== 'string') return null
  const raw = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * تفسیر امن مهر زمانی عددی (ثانیه یا میلی‌ثانیه از مبدأ یونیکس).
 *
 * `new Date(undefined * 1000)` تاریخ نامعتبر می‌سازد و `Intl` روی آن
 * RangeError می‌دهد؛ چون این‌ها داخل JSX صدا زده می‌شوند، یک رکورد ناقصِ
 * آمده از سرور کافی است تا کل صفحه از کار بیفتد.
 */
export function parseStamp(value: unknown): Date | null {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  // مقادیر کوچک‌تر از حدود سال ۲۰۰۱ بر حسب میلی‌ثانیه، یعنی واحد ثانیه است
  const d = new Date(n < 1e11 ? n * 1000 : n)
  return Number.isNaN(d.getTime()) ? null : d
}

/** فقط ساعت (HH:MM یا HH:MM:SS) — روی ورودی خراب «—» می‌دهد، نه استثنا. */
export function faTime(value: unknown, withSeconds = false): string {
  const d = value instanceof Date
    ? (Number.isNaN(value.getTime()) ? null : value)
    : (typeof value === 'string' ? parseDate(value) : parseStamp(value))
  if (!d) return '—'
  try {
    return new Intl.DateTimeFormat('fa-IR', {
      hour: '2-digit', minute: '2-digit',
      ...(withSeconds ? { second: '2-digit' } : {}),
    }).format(d)
  } catch {
    return '—'
  }
}

export function timeAgo(iso?: string | null): string {
  const parsed = parseDate(iso)
  if (!parsed) return '—'
  const s = Math.floor((Date.now() - parsed.getTime()) / 1000)
  if (s < 60) return 'لحظاتی پیش'
  if (s < 3600) return `${fa(Math.floor(s / 60))} دقیقه پیش`
  if (s < 86400) return `${fa(Math.floor(s / 3600))} ساعت پیش`
  return `${fa(Math.floor(s / 86400))} روز پیش`
}

export const clsx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ')

export const shortAddr = (a?: string, n = 8) =>
  !a ? '—' : a.length <= n * 2 ? a : `${a.slice(0, n)}…${a.slice(-6)}`

/** ۶۰۳۷-۹۹۷۱-****-۷۸۹۰ — روی ورودی خراب «—» می‌دهد، نه استثنا */
export const cardNumber = (v?: string | null) =>
  typeof v === 'string' && v ? fa(v.replace(/(\d{4})(?=\d)/g, '$1-')) : '—'


/**
 * تضمینِ آرایه‌بودن پاسخ سرور.
 *
 * چند صفحه مستقیماً روی پاسخ API متد `map`/`filter` صدا می‌زدند. اگر سرور به
 * هر دلیلی شیء یا null برگرداند (خطای ۵۰۰، تغییر قالب، پاسخ خالی)، همان لحظه
 * استثنا پرتاب می‌شد و صفحه سفید می‌ماند. این کمکی همیشه آرایه برمی‌گرداند.
 */
export function asArray<T = any>(v: any, key?: string): T[] {
  if (Array.isArray(v)) return v as T[]
  if (v && typeof v === 'object') {
    if (key && Array.isArray(v[key])) return v[key] as T[]
    for (const k of ['data', 'items', 'results', 'rows']) {
      if (Array.isArray(v[k])) return v[k] as T[]
    }
  }
  return []
}

/* ══════════════════════════════════════════════════ تاریخ شمسی روی نمودار */

/**
 * برچسب‌های محور زمانِ نمودار، به تقویم **شمسی**.
 *
 * چرا: کاربر ایرانی «۲۵ مرداد» را می‌فهمد، «Aug 16» را باید در ذهنش تبدیل
 * کند. نمودارهای مبتنی بر تریدینگ‌ویو (از جمله نوبیتکس) محور زمانشان
 * میلادی است؛ این یکی از جاهایی است که می‌شود از آن‌ها جلو زد.
 *
 * سازنده‌های `Intl` گران‌اند و این تابع برای هر برچسبِ محور صدا زده می‌شود،
 * پس یک بار ساخته و نگه داشته می‌شوند.
 */
const _fmt: Record<string, Intl.DateTimeFormat> = {}

/**
 * ⚠ منطقه زمانی عمداً UTC است.
 *
 * کتابخانه نمودار، **جای** برچسب‌ها را بر مبنای UTC حساب می‌کند (پشتیبانی
 * منطقه زمانی ندارد). اگر ما همان لحظه را به وقت محلی قالب‌بندی کنیم،
 * برچسب‌ها با جایی که نشسته‌اند نمی‌خوانند: خطِ «شروع روز» ساعت ۰۳:۳۰
 * می‌شود و برچسب روز، یک روز عقب می‌افتد. کلیدِ حافظه هم از خودِ گزینه‌ها
 * ساخته می‌شود تا دو کاربردِ متفاوت به‌اشتباه یک قالب‌بند مشترک نگیرند.
 */
const _get = (opt: Intl.DateTimeFormatOptions): Intl.DateTimeFormat | null => {
  const key = JSON.stringify(opt)
  if (!_fmt[key]) {
    try { _fmt[key] = new Intl.DateTimeFormat('fa-IR', { timeZone: 'UTC', ...opt }) }
    catch { return null }
  }
  return _fmt[key]
}

/** انواع برچسب محور در کتابخانه نمودار: ۰ سال، ۱ ماه، ۲ روز، ۳ ساعت، ۴ ثانیه */
export function faChartTick(epochSec: number, kind: number): string {
  const d = parseStamp(epochSec)
  if (!d) return ''
  try {
    if (kind === 0) return _get({ year: 'numeric' })?.format(d) ?? ''
    if (kind === 1) return _get({ month: 'short' })?.format(d) ?? ''
    if (kind === 2) return _get({ day: 'numeric', month: 'short' })?.format(d) ?? ''
    if (kind === 4) {
      return _get({ hour: '2-digit', minute: '2-digit', second: '2-digit' })?.format(d) ?? ''
    }
    return _get({ hour: '2-digit', minute: '2-digit' })?.format(d) ?? ''
  } catch { return '' }
}

/** تاریخ و ساعت کامل شمسی برای برچسب زیر نشانگر */
export function faStamp(epochSec: number): string {
  const d = parseStamp(epochSec)
  if (!d) return ''
  try {
    const date = _get({ year: 'numeric', month: '2-digit', day: '2-digit' })?.format(d) ?? ''
    const time = _get({ hour: '2-digit', minute: '2-digit' })?.format(d) ?? ''
    return `${date} ${time}`
  } catch { return '' }
}
